
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const Hero = () => {
  const elementsRef = useRef<(HTMLDivElement | null)[]>([]);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      elementsRef.current.forEach((el, index) => {
        if (!el) return;
        
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.85;
        
        if (isVisible) {
          setTimeout(() => {
            el.classList.add("revealed");
          }, index * 150);
        }
      });
    };
    
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background gradient - improved for dark mode */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-950 -z-10 transition-colors duration-300"></div>
      
      {/* Animated shapes - improved visibility */}
      <div className="absolute top-1/4 -right-20 w-96 h-96 bg-primary/10 dark:bg-primary/20 rounded-full filter blur-3xl animate-float transition-colors duration-300"></div>
      <div className="absolute bottom-1/4 -left-20 w-72 h-72 bg-accent/10 dark:bg-accent/20 rounded-full filter blur-3xl animate-float animation-delay-300 transition-colors duration-300"></div>
      
      <div className="container mx-auto px-6 py-20 md:py-40 text-center">
        <h1 
          ref={el => elementsRef.current[0] = el} 
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 max-w-4xl mx-auto reveal-on-scroll text-gray-800 dark:text-gray-50 transition-colors duration-300"
        >
          {t('arbitrinity_hero_title')}
        </h1>
        
        <div 
          ref={el => elementsRef.current[1] = el} 
          className="flex justify-center mt-8 reveal-on-scroll"
        >
          <Button 
            size="lg" 
            variant="default" 
            className="mr-4"
            onClick={() => {
              const solutionSection = document.getElementById('solution');
              if (solutionSection) {
                solutionSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {t('arbitrinity_hero_cta1')}
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="border-primary/20 hover:bg-primary/5 dark:border-gray-400 dark:text-gray-200 dark:hover:bg-gray-700/30 transition-colors duration-300"
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {t('arbitrinity_hero_cta2')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
